/**
 * IntlProvider
 *
 * React context provider that makes the current locale and country configuration
 * available to all components in the tree.
 *
 * Reads the locale from the URL (via React Router params) or falls back to
 * DEFAULT_LOCALE. Stores the user's preference in a cookie for persistence.
 */

import { createContext, useMemo, useCallback, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { CountryConfig, IntlContextValue } from '@template-project/storefront-shared';
import { DEFAULT_LOCALE, isSupportedLocale } from './intl.config';
import type { SupportedLocale } from './intl.config';
import { COUNTRY_CONFIG } from './country-config';
import { formatLocalizedPath, stripLocaleFromPath } from './helpers';
import { changeLanguage } from '@/lib/i18n/client';

const LOCALE_COOKIE_NAME = 'preferred_locale';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

export const IntlContext = createContext<IntlContextValue | undefined>(undefined);

function setCookie(name: string, value: string, maxAge: number): void {
  document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
}

export function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : undefined;
}

interface IntlProviderProps {
  /** The locale resolved from the URL or other source */
  locale?: string;
  children: ReactNode;
}

/**
 * Resolve the effective locale from a provided value.
 * Falls back to DEFAULT_LOCALE if the provided value is not a supported locale.
 */
export function resolveLocale(locale?: string): SupportedLocale {
  if (locale && isSupportedLocale(locale)) {
    return locale;
  }
  return DEFAULT_LOCALE;
}

export function IntlProvider({ locale: localeProp, children }: IntlProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const currentLocale = resolveLocale(localeProp);
  const countryConfig: CountryConfig = COUNTRY_CONFIG[currentLocale];

  // Keep i18next language in sync with the current locale
  useEffect(() => {
    changeLanguage(currentLocale);
  }, [currentLocale]);

  const localizedPath = useCallback(
    (path: string): string => formatLocalizedPath(path, currentLocale),
    [currentLocale]
  );

  const changeLocale = useCallback(
    (newLocale: string) => {
      if (!isSupportedLocale(newLocale)) {
        return;
      }

      setCookie(LOCALE_COOKIE_NAME, newLocale, COOKIE_MAX_AGE);

      const basePath = stripLocaleFromPath(location.pathname);
      const newPath = formatLocalizedPath(basePath, newLocale);
      navigate(newPath + location.search + location.hash);
    },
    [navigate, location]
  );

  const value = useMemo<IntlContextValue>(
    () => ({
      locale: currentLocale,
      countryConfig,
      localePrefix: countryConfig.localePrefix,
      localizedPath,
      changeLocale,
    }),
    [currentLocale, countryConfig, localizedPath, changeLocale]
  );

  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
}
